import { WIDGET_KEYS } from "../../../other/constants/WidgetKeys.jsx";
import { useCallback } from "react";
import { PERSISTENCE_STATE_KEYS } from "../../../other/constants/PersistenceStateKeys.jsx";
import SwivuModal from "../../../react/components/editors/SwivuModal.jsx";
import EditorBase from "../../../bryntum-lib/editors/base/EditorBase.jsx";
import {
  DEEP_LINK_URL_KEYS,
  EDITOR_CONTEXT_MENU_ITEM_KEYS,
  SETTING_TAB_WIDGET_KEYS,
  SETTING_TAB_WIDGET_TABS_DETAILS
} from "../../../other/constants/OtherKeys.jsx";
import SwivuAlertNotifications from "../../../bryntum-lib/editors/settingsEditor/SwivuAlertNotifications.jsx";
import SettingsBoardTab from "../../../bryntum-lib/editors/settingsEditor/SettingsBoardTab.jsx";
import { assignViewsState, getEntityShareLink } from "../../../other/utils/otherUtils.jsx";
import { StringHelper } from "@bryntum/schedulerpro";
import { userPreferences } from "../../context/bryntumContext.jsx";
import SettingsCanEditViewPrefTab from "../../../bryntum-lib/editors/settingsEditor/SettingsCanEditViewPrefTab.jsx";
import SettingsPersonalScheduleTab from "../../../bryntum-lib/editors/settingsEditor/SettingsPersonalScheduleTab.jsx";


const getListingView = (params = {}) => {
  return {
    readOnly: true,
    scrollable: true,
    cls: "@md:flex-[1_0_35%] @sm:flex-[1_0_100%] dark:border-0 border border-solid border-[#ccc]",
    type: WIDGET_KEYS.swivuGrid,
    height: "100%",
    selectionMode: {
      row: true,
      multiSelect: false
    },
    features: {
      filter: true,
      rowReorder: false,
      stripe: true,
      cellMenu: false,
    },
    ...params
  };
};

const getListingDetailView = (params = {}) => {
  return {
    type: "container",
    cls: "@md:h-full @sm:h-auto @md:flex-[1_0_55%] @sm:flex-[1_0_100%] @md:mt-0 @sm:mt-[1em] bg-[#fefefe] dark:bg-[#222] dark:border-0 border border-solid border-[#ccc]",
    defaults: {
      hidden: true,
      width: "100%",
      height: "100%",
      scrollable: true
    },
    itemCls: "p-0",
    ...params
  };
};

class SwivuSettingPersonalEditor extends EditorBase {
  static get $name() {
    return "SwivuSettingPersonalEditor";
  }

  static get type() {
    return WIDGET_KEYS.swivuSettingEditorPopup;
  }

  static get configurable() {
    return {
      title: "My Settings",
      scrollable: true,
      minWidth: "30em",
      maxWidth: "100vw",
      width: "93em",
      minHeight: "600px",
      maxHeight: "100vh",
      height: "min(100vh, 800px)",
      cls: "resize",
      closable: true,
      autoClose: false,
      draggable: true,
      stateId: PERSISTENCE_STATE_KEYS.settingPersonal,
      contentElementCls: "@container flex-row",

      items: {
        descriptionText: {
          flex: "1 0 100%",
          html: "Personal settings impact your user experience on this board only."
        },
        preferencePersonalTabsGrid: getListingView({
          stateId: PERSISTENCE_STATE_KEYS.settingsTabUserGrid,
          onSelectionChange: "up.onTabGridSelectionChange",
          columns: [
            { id: "tab-user-settings-category", text: "Category", type: WIDGET_KEYS.swivuSortColumn,  field: "category" },
            { id: "tab-user-settings-topic", type: WIDGET_KEYS.swivuSortColumn, text: "Topic", field: "topic" }
          ]
        }),
        preferencePersonalDetailView: getListingDetailView({
          items: {
            [SETTING_TAB_WIDGET_KEYS.alertNotifications]: { type: SwivuAlertNotifications.type },
            [SETTING_TAB_WIDGET_KEYS.boardTab]: { type: SettingsBoardTab.type },
            [SETTING_TAB_WIDGET_KEYS.personalSchedularTab]: { type: SettingsPersonalScheduleTab.type },
            [SETTING_TAB_WIDGET_KEYS.canEditViewTab]: { type: SettingsCanEditViewPrefTab.type }
          }
        }),
      }
    };
  }

  editRecord(record, context, otherParams) {
    super.editRecord(record, context, otherParams);
    this.otherParams = otherParams;
    const { preferencePersonalTabsGrid, preferencePersonalDetailView } = this.widgetMap;

    preferencePersonalTabsGrid.store.data = Object.entries(SETTING_TAB_WIDGET_TABS_DETAILS.userPreferences).map(([prefKey, prefValueObj]) => ({
      tabWidgetRef: prefKey,
      id: prefKey,
      category: prefValueObj.category,
      topic: prefValueObj.topic
    }));

    preferencePersonalDetailView.eachWidget((widget) => {
      if (widget.isSettingsTabBase) widget.editRecord(record, context, otherParams);
    });
    if (!this.isBoardPrefListenerAttached) {
      this.isBoardPrefListenerAttached = userPreferences.boardPreferencesStore.on("change", () => {
      }, this);
    }

    assignViewsState([preferencePersonalTabsGrid], context.stateProvider);

    // editor context menu config
    this.configureEditorContextMenu(record, context, {
      recordStore: context.project.resourceStore,
      items: {
        [EDITOR_CONTEXT_MENU_ITEM_KEYS.shareItem]: {
          enabled: true,
          editorParams() {
            const selectedRecord = preferencePersonalTabsGrid.selectedRecord;
            const tabDeepLinkSlugRec = { id: selectedRecord ? preferencePersonalDetailView.getWidgetById(selectedRecord.id).swivuDeepLinkSlug : "" };
            return {
              message: getEntityShareLink({
                record: tabDeepLinkSlugRec,
                context,
                entityType: DEEP_LINK_URL_KEYS.personalSetting,
                entityDetail: {
                  entityVisibleName: "Board Setting",
                  name: StringHelper.capitalize(tabDeepLinkSlugRec.id)
                }
              })
            };
          }
        }
      }
    });
    preferencePersonalTabsGrid.selectRow({ record: preferencePersonalTabsGrid.selectedRecord || preferencePersonalTabsGrid.store.first });

    // select grid record by share link value
    if (otherParams.selectTabByTypeOnOpen) {

      const personalPrefRecord = preferencePersonalTabsGrid.store.getById(otherParams.selectTabByTypeOnOpen);
      if (personalPrefRecord) {
        setTimeout(() => { // changing tab just before selecting a row breaks into console
          preferencePersonalTabsGrid.selectRow({ record: personalPrefRecord });
        }, 10);
      }
      this.otherParams.selectTabByTypeOnOpen = null;
    }
  }

  onTabGridSelectionChange({ action, selected, deselected }) {
    if (action === "select") {
      const { preferencePersonalDetailView } = this.widgetMap;
      const [selectedRecord] = selected;
      const [deselectedRecord] = deselected;
      if (selectedRecord) {
        const selectedTab = SETTING_TAB_WIDGET_TABS_DETAILS.userPreferences[selectedRecord.tabWidgetRef] || SETTING_TAB_WIDGET_TABS_DETAILS.boardPreferences[selectedRecord.tabWidgetRef];
        this.title = `<div class="flex justify-between">
            <div>My Settings</div>
            <div class="mr-[2em]">${selectedTab.category} - ${selectedTab.topic}</div>
            <div></div>
        </div>`;
        preferencePersonalDetailView.getWidgetByRef(selectedRecord.tabWidgetRef).hidden = false;
      }
      if (deselectedRecord) preferencePersonalDetailView.getWidgetByRef(deselectedRecord.tabWidgetRef).hidden = true;
    }
  }
}

SwivuSettingPersonalEditor.initClass();

const SwivuSettingPersonalPopup = ({ context, record, onClose, isShow, editorInstance }) => {
  const { selectTabByTypeOnOpen } = record;
  const onShowHandler = useCallback((editor, otherParams = {}) => {
    editor.project = context.project;
    editor.editRecord({}, context, Object.assign(otherParams, { selectTabByTypeOnOpen })); // Settings Editor is associated with the whole project; there's no specific "record" to edit.
    editorInstance.editorRef = editor;
  }, [context, editorInstance]);
  return (
    <SwivuModal
      widgetType={WIDGET_KEYS.swivuSettingEditorPopup}
      setFontSize={context.setFontSize}
      rowHeight={context.rowHeight}
      isShow={isShow}
      onHide={onClose}
      onShow={onShowHandler}
    />
  );
};

export default SwivuSettingPersonalPopup;
